import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAlbumContext } from "@/contexts/AlbumProvider"
import { useTheme } from '@/contexts/ThemeProvider';
import { isAppRoot, isPanelPage } from "@/lib/utils"
import { useLocation } from 'react-router-dom';

const settingsCache: Record<string, Record<string, string>> = {};

export const mergeSettingsCache = (albumId: string, updates: Record<string, string>) => {
  settingsCache[albumId] = {
    ...(settingsCache[albumId] || {}),
    ...updates,
  };
};

export const invalidateSettingsCache = (albumId?: string) => {
  if (albumId) {
    delete settingsCache[albumId];
    return;
  }

  Object.keys(settingsCache).forEach((key) => {
    delete settingsCache[key];
  });
};

export const useSettings = () => {
  const { albumId, loading: isResolvingAlbum } = useAlbumContext();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const { resetTheme } = useTheme();
  const location = useLocation();
  const pathname = location.pathname;
  const isRoot = isAppRoot({ pathname });
  const isPanel = isPanelPage({ pathname });

  useEffect(() => {
    // console.log('📊 useSettings: albumId changed:', { albumId, loading });

    if (!albumId) {
      if (isResolvingAlbum && !isRoot && !isPanel) {
        return;
      }

      setLoading(false);
      // Do not reset theme on / (root) or /panel pages
      if (!isRoot && !isPanel) {
        resetTheme();
      }
      return;
    }

    if (settingsCache[albumId]) {
      setSettings(settingsCache[albumId]);
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        // console.log('📊 useSettings: Fetching settings for albumId:', albumId);
        // console.log('📊 useSettings: Current auth state:', await supabase.auth.getUser());

        const { data, error } = await supabase
          .from('settings')
          .select('key, value, album_id')
          .eq('album_id', albumId)
          .neq('key', 'screenKey');

        // console.log('📊 useSettings: Raw data returned:', data);
        // console.log('📊 useSettings: Any error?', error);


        if (error) throw error;

        const settingsObj = data?.reduce((acc, setting) => {
          acc[setting.key] = setting.value;
          return acc;
        }, {} as Record<string, string>) || {};

        settingsCache[albumId] = settingsObj;

        // console.log('📊 useSettings: Fetched settings:', settingsObj);
        setSettings(settingsObj);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [albumId, isPanel, isResolvingAlbum, isRoot, pathname]);

  const getSetting = useCallback((key: string, defaultValue: string = '') => {
    if (loading) {
      return
    }
    // // console.log('📊 useSettings: getSetting called:', { key, value: settings[key], defaultValue, loading });
    return settings[key] ?? defaultValue;
  }, [settings, loading]);

  const getBooleanSetting = useCallback((key: string, defaultValue: boolean = false) => {
    const value = settings[key];
    if (value === 'true') return true;
    if (value === 'false') return false;
    return defaultValue;
  }, [settings]);

  return { settings, loading, getSetting, getBooleanSetting };
};
